import React, { useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Stack,
  Button,
  Grid,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Fade,
  CircularProgress,
  Tooltip,
  Avatar,
} from "@mui/material";
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FilterList as FilterListIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  History as AccessTime,
  People as PeopleIcon,
  Label as LabelIcon,
  MoreHoriz as MoreHorizIcon,
} from "@mui/icons-material";
import { DataGrid } from '@mui/x-data-grid';
import UserHistory from "./DateUsers/UserHistory";

const UsersManagement = ({
  filteredUsers = [],
  isLoading,
  setActiveTab,
  handleRefresh,
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  profileFilter,
  setProfileFilter,
  clearFilters,
  uniqueProfiles = [],
  handleEditUser,
  handleOpenHistory,
}) => {
  const [openHistory, setOpenHistory] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [sortModel, setSortModel] = useState([]);

  const handleHistoryUser = (user) => {
    setSelectedUser(user);
    setOpenHistory(true);
  };

  // Función para renderizar etiquetas con límite y tooltip
  const renderLabels = (labels, maxVisible = 2) => {
    if (!labels || labels.length === 0) {
      return (
        <Chip 
          label="Sin etiquetas" 
          size="small" 
          variant="outlined" 
          color="default"
          icon={<LabelIcon />}
          sx={{
            backgroundColor: 'rgba(156, 163, 175, 0.1)',
            borderColor: '#d1d5db',
            color: '#6b7280',
            fontSize: '0.7rem',
          }}
        />
      );
    }

    if (labels.length <= maxVisible) {
      return (
        <Stack direction="row" spacing={0.5} flexWrap="wrap">
          {labels.map((label, index) => (
            <Chip
              key={index}
              label={label.name || label}
              size="small"
              color={label.isProfile ? "success" : "primary"}
              variant="filled"
              sx={{
                fontWeight: 600,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontSize: '0.7rem',
                maxWidth: 80,
                '& .MuiChip-label': {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              }}
            />
          ))}
        </Stack>
      );
    }

    // Si hay más etiquetas de las que se pueden mostrar
    const visibleLabels = labels.slice(0, maxVisible);
    const hiddenCount = labels.length - maxVisible;

    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {visibleLabels.map((label, index) => (
          <Chip
            key={index}
            label={label.name || label}
            size="small"
            color={label.isProfile ? "success" : "primary"}
            variant="filled"
            sx={{
              fontWeight: 600,
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              fontSize: '0.7rem',
              maxWidth: 80,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              },
            }}
          />
        ))}
        
        <Tooltip title={`${hiddenCount} etiquetas más`}>
          <Chip
            label={`+${hiddenCount}`}
            size="small"
            variant="outlined"
            color="default"
            icon={<MoreHorizIcon />}
            sx={{
              backgroundColor: 'rgba(156, 163, 175, 0.1)',
              borderColor: '#d1d5db',
              color: '#6b7280',
              fontSize: '0.7rem',
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'rgba(156, 163, 175, 0.2)',
              },
            }}
          />
        </Tooltip>
      </Stack>
    );
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      width: 80,
      sortable: true,
      renderCell: (params) => (
        <Box
          sx={{
            backgroundColor: 'rgba(77, 171, 164, 0.1)',
            borderRadius: '50%',
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 600,
            color: '#4DABA4',
            boxShadow: '0 2px 8px rgba(77, 171, 164, 0.2)',
          }}
        >
          {params.value}
        </Box>
      ),
    },
    { 
      field: 'name', 
      headerName: 'Usuario', 
      flex: 1,
      sortable: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              width: 40,
              height: 40,
              backgroundColor: 'rgba(77, 171, 164, 0.1)',
              color: '#4DABA4',
              fontWeight: 600,
              fontSize: '1rem',
              boxShadow: '0 2px 8px rgba(77, 171, 164, 0.2)',
            }}
          >
            {params.value ? params.value.charAt(0).toUpperCase() : '?'}
          </Avatar>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#1f2937' }}>
              {params.value || 'Sin nombre'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#6b7280', fontSize: '0.7rem' }}>
              {params.row?.email || 'Sin email'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      field: 'cedula',
      headerName: 'Cédula',
      width: 150,
      sortable: true,
      valueGetter: (params) => params?.row?.cedula || 'N/A',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          sx={{
            backgroundColor: 'rgba(107, 114, 128, 0.05)',
            borderColor: '#d1d5db',
            color: '#374151',
            fontWeight: 500,
            fontSize: '0.75rem',
            '&:hover': {
              backgroundColor: 'rgba(107, 114, 128, 0.1)',
            },
          }}
        />
      ),
    },
    {
      field: 'label',
      headerName: 'Etiquetas',
      width: 220,
      sortable: true,
      renderCell: (params) => {
        const labels = params?.row?.label;

        if (Array.isArray(labels)) {
          return renderLabels(labels, 2);
        }

        if (labels && typeof labels === "object") {
          return renderLabels([labels], 1);
        }

        if (labels && typeof labels === "string") {
          return renderLabels([labels], 1);
        }

        return renderLabels([]);
      }
    },
    {
      field: 'roleName',
      headerName: 'Rol',
      width: 160,
      sortable: true,
      valueGetter: (params) => params?.row?.role?.name || 'Sin rol',
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="filled"
          sx={{
            backgroundColor: 'rgba(77, 171, 164, 0.12)',
            color: '#1f2937',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'capitalize',
          }}
        />
      ),
    },
    {
      field: 'status',
      headerName: 'Estado',
      width: 120,
      sortable: true,
      valueGetter: (params) => params?.row?.status || 'activo',
      renderCell: (params) => {
        const status = params.value;
        const isActive = status === 'activo';
        
        return (
          <Chip
            label={status}
            size="small"
            color={isActive ? "success" : "default"}
            variant="filled"
            icon={isActive ? undefined : <MoreHorizIcon />}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              backgroundColor: isActive ? '#10b981' : '#6b7280',
              color: 'white',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          />
        );
      }
    },
    {
      field: 'createdAt',
      headerName: 'Fecha de Creación',
      width: 180,
      sortable: true,
      valueGetter: (params) =>
        params?.row?.createdAt
          ? new Date(params.row.createdAt).toLocaleString('es-ES', {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })
          : 'N/A',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, color: '#374151' }}>
            {params.value}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
            Creado
          </Typography>
        </Box>
      ),
    },
    {
      field: 'edit',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Tooltip title="Editar usuario">
            <Button
              variant="contained"
              size="small"
              onClick={() => handleEditUser(params.row)}
              sx={{
                borderRadius: 2,
                backgroundColor: '#4DABA4',
                boxShadow: '0 2px 8px rgba(77, 171, 164, 0.3)',
                minWidth: 'auto',
                px: 1.5,
                '&:hover': {
                  backgroundColor: '#3e958e',
                  boxShadow: '0 4px 12px rgba(77, 171, 164, 0.4)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
            >
              Editar
            </Button>
          </Tooltip>
        </Stack>
      ),
    },
    {
      field: 'history',
      headerName: 'Historial',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title="Ver historial">
          <Button
            variant="outlined"
            size="small"
            onClick={() => handleHistoryUser(params.row)}
            sx={{
              borderRadius: 2,
              borderColor: '#6B7280',
              color: '#6B7280',
              minWidth: 'auto',
              px: 1.5,
              '&:hover': {
                borderColor: '#374151',
                backgroundColor: 'rgba(107, 114, 128, 0.08)',
                transform: 'translateY(-1px)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
            startIcon={<AccessTime sx={{ fontSize: 16 }} />}
          >
            Ver
          </Button>
        </Tooltip>
      ),
    },
  ];

  if (isLoading) {
    return (
      <Box 
        className="flex flex-col items-center justify-center min-h-[400px] gap-6"
        sx={{
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          borderRadius: 3,
          border: '1px solid #e2e8f0',
        }}
      >
        <CircularProgress 
          size={80} 
          sx={{ 
            color: '#4DABA4',
            filter: 'drop-shadow(0 4px 8px rgba(77, 171, 164, 0.3))',
          }} 
        />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Cargando usuarios...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Preparando la información para ti
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Fade in timeout={700}>
      <Box 
        className="space-y-6"
        sx={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: 4,
          border: '1px solid #e2e8f0',
          overflow: 'hidden',
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.08)',
        }}
      >
        {/* Header con gradiente */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #4DABA4 0%, #3e958e 100%)',
            p: 4,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: -50,
              right: -50,
              width: 200,
              height: 200,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.1)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 150,
              height: 150,
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.05)',
            }}
          />
          
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
            <Box>
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
                <PeopleIcon sx={{ fontSize: 32, opacity: 0.9 }} />
                <Typography variant="h4" sx={{ fontWeight: 700, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                  Gestión de Usuarios
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Total: {filteredUsers.length} usuarios registrados
              </Typography>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={isLoading}
              sx={{
                borderRadius: 3,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                color: 'white',
                px: 3,
                py: 1.5,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(0, 0, 0, 0.15)',
                },
                transition: 'all 0.3s ease-in-out',
                fontWeight: 600,
              }}
            >
              Actualizar
            </Button>
          </Stack>
        </Box>

        {/* Filtros con diseño mejorado */}
        <Box sx={{ px: 4, pb: 2 }}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 3, 
              borderRadius: 3,
              background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
              border: '1px solid #e2e8f0',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, color: '#374151', fontWeight: 600 }}>
              🔍 Filtros de Búsqueda
            </Typography>
            
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={3}>
                <TextField
                  fullWidth
                  size="medium"
                  placeholder="Buscar por nombre o email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#4DABA4' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 3,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(77, 171, 164, 0.1)',
                      },
                    },
                  }}
                />
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ color: '#6b7280' }}>Estado</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    sx={{
                      borderRadius: 3,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    <MenuItem value="activo">Activo</MenuItem>
                    <MenuItem value="desactivado">Desactivado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ color: '#6b7280' }}>Perfil</InputLabel>
                  <Select
                    value={profileFilter}
                    onChange={(e) => setProfileFilter(e.target.value)}
                    sx={{
                      borderRadius: 3,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <MenuItem value="all">Todos</MenuItem>
                    {uniqueProfiles.map((profile) => (
                      <MenuItem key={profile} value={profile}>
                        {profile}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={2}>
                <FormControl fullWidth size="medium">
                  <InputLabel sx={{ color: '#6b7280' }}>Mostrar</InputLabel>
                  <Select
                    value={paginationModel.pageSize}
                    onChange={(e) => setPaginationModel(prev => ({ ...prev, pageSize: e.target.value }))}
                    sx={{
                      borderRadius: 3,
                      backgroundColor: 'white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                      },
                    }}
                  >
                    <MenuItem value={5}>5 elementos</MenuItem>
                    <MenuItem value={10}>10 elementos</MenuItem>
                    <MenuItem value={25}>25 elementos</MenuItem>
                    <MenuItem value={50}>50 elementos</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={3}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<FilterListIcon />}
                  onClick={clearFilters}
                  sx={{
                    height: 56,
                    borderRadius: 3,
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    backgroundColor: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      backgroundColor: '#f9fafb',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    },
                    transition: 'all 0.2s ease-in-out',
                    fontWeight: 600,
                  }}
                >
                  Limpiar Filtros
                </Button>
              </Grid>
            </Grid>
          </Paper>
        </Box>

        {/* Tabla con estilos mejorados */}
        <Box sx={{ px: 4, pb: 4 }}>
          <div className="h-[600px]">
            <DataGrid
              rows={filteredUsers}
              columns={columns}
              pageSizeOptions={[5, 10, 25, 50]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={setSortModel}
              disableRowSelectionOnClick
              getRowId={(row) => row.id}
              loading={isLoading}
              slots={{
                noRowsOverlay: () => (
                  <Box className="flex flex-col gap-4 justify-center items-center h-full">
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2,
                      }}
                    >
                      <PersonIcon sx={{ fontSize: 40, color: "#9ca3af" }} />
                    </Box>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No se encontraron usuarios
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ maxWidth: 300 }}>
                      Intenta ajustar los filtros de búsqueda o agregar nuevos usuarios al sistema
                    </Typography>
                  </Box>
                )
              }}
              sx={{
                backgroundColor: 'white',
                borderRadius: 3,
                border: '1px solid #e2e8f0',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                '& .MuiDataGrid-columnHeaders': {
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderBottom: '2px solid #e2e8f0',
                  minHeight: '60px !important',
                  maxHeight: '60px !important',
                },
                '& .MuiDataGrid-columnHeader': {
                  borderRight: '1px solid #e2e8f0',
                  '&:last-child': {
                    borderRight: 'none',
                  },
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  color: '#374151 !important',
                  fontWeight: '700 !important',
                  fontSize: '0.875rem !important',
                },
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid #f1f5f9',
                  borderRight: '1px solid #f1f5f9',
                  '&:last-child': {
                    borderRight: 'none',
                  },
                },
                '& .MuiDataGrid-row': {
                  minHeight: '70px !important',
                  maxHeight: '70px !important',
                  '&:hover': {
                    backgroundColor: 'rgba(77, 171, 164, 0.04)',
                    transform: 'scale(1.001)',
                    transition: 'all 0.2s ease-in-out',
                  },
                  '&:nth-of-type(even)': {
                    backgroundColor: '#fafbfc',
                  },
                },
                '& .MuiDataGrid-footerContainer': {
                  borderTop: '2px solid #e2e8f0',
                  backgroundColor: '#f8fafc',
                  minHeight: '60px !important',
                },
                '& .MuiDataGrid-sortIcon': {
                  color: '#4DABA4 !important',
                },
                '& .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
              }}
            />
          </div>
        </Box>

        <UserHistory open={openHistory} onClose={() => setOpenHistory(false)} selectedUser={selectedUser} />
      </Box>
    </Fade>
  );
};

export default UsersManagement;
